import imageStorage from "./ImageStorage.js";
import { fetchCFImage, uploadImageToCloudflare } from "./cloudflare.js";
import { isIdentityTransform } from "./utility.js";
import { Product } from "./Product.js";

const STORAGE_KEY = "cyokit-saved-designs";

/**
 * CYODesign represents a design configuration.
 * Used both as a runtime state container (via CYODesign.current singleton)
 * and as a saveable/restorable design (via save/restore methods).
 */
class CYODesign {
    // Static enums
    static Style = {
        removeBackground: "remove-background",
        full: "full",
        snapshot: "snapshot"
    };

    static Placement = {
        center: "center",
        bottom: "bottom",
        corner: "corner",
        border: "border",
        pattern: "pattern"
    };

    static StickerOrPhoto = {
        sticker: "sticker",
        photo: "photo",
        both: "both"
    };

    static WalletPrintSurface = {
        split: "split",
        both: "both",
        wallet: "wallet",
        grip: "grip"
    };

    /**
     * Schema version history:
     * Version | Changes
     * --------|--------
     * 1       | Initial version (CYO4.0)
     * 2       | Added walletPrintSurface (v1 designs default to "wallet" for backward compat)
     */
    static CURRENT_VERSION = 2;

    /**
     * Migrate saved design data from one version to another.
     * Only applies migrations when data has an explicit __version (i.e., loaded saved design).
     * Fresh designs (no __version) skip migration and use current defaults.
     * @param {Object} data - Raw saved design data
     * @param {number} fromVersion - Source version (defaults to data.__version, or CURRENT_VERSION if not set)
     * @param {number} toVersion - Target version (defaults to CURRENT_VERSION)
     * @returns {Object} - Migrated data
     */
    static migrate(data, fromVersion = null, toVersion = null) {
        const hasExplicitVersion = data.__version !== undefined;
        fromVersion = fromVersion ?? (hasExplicitVersion ? data.__version : CYODesign.CURRENT_VERSION);
        toVersion = toVersion ?? CYODesign.CURRENT_VERSION;

        if (fromVersion >= toVersion) {
            return data;
        }

        let migrated = { ...data };

        // v1 → v2: Add walletPrintSurface (CYO4.0 always used wallet-only mode)
        if (fromVersion < 2 && toVersion >= 2) {
            migrated.walletPrintSurface = migrated.walletPrintSurface ?? CYODesign.WalletPrintSurface.wallet;
        }

        migrated.__version = toVersion;
        return migrated;
    }

    constructor(data = {}) {
        const migrated = CYODesign.migrate(data);
        const { Style, Placement } = CYODesign;

        // Runtime design properties
        this.subjectStyle = migrated.subjectStyle ?? Style.removeBackground;
        this.subjectPlacement = migrated.subjectPlacement ?? Placement.center;
        this.subjectOutlines = migrated.subjectOutlines ?? false;
        this.stickerSet = migrated.stickerSet ?? null;
        this.stickerPlacement = migrated.stickerPlacement ?? Placement.border;
        this.stickerOutlines = migrated.stickerOutlines ?? false;
        this.galleryImageID = migrated.galleryImageID ?? null;
        this.remixSeed = migrated.remixSeed ?? null;
        this.stickerSeed = migrated.stickerSeed ?? null;
        this.selectedColor = migrated.selectedColor ?? null;
        this.selectedColorAlt = migrated.selectedColorAlt ?? null;
        this.walletPrintSurface = migrated.walletPrintSurface ?? CYODesign.WalletPrintSurface.split;

        // Debug options (not persisted)
        const debugData = migrated.debugOptions ?? {};
        this.debugOptions = {
            disableRotations: debugData.disableRotations ?? false,
            drawBgs: debugData.drawBgs ?? false,
            showSafeArea: debugData.showSafeArea ?? false,
            showBleedInsets: debugData.showBleedInsets ?? false,
            hideProduct: debugData.hideProduct ?? false,
            hideMask: debugData.hideMask ?? false,
            hideOverlay: debugData.hideOverlay ?? false,
            hideColoredOverlay: debugData.hideColoredOverlay ?? false,
            isolateMask: debugData.isolateMask ?? false
        };

        // Persistence properties (for saved designs)
        this.timestamp = migrated.timestamp ?? null;
        this.savedAt = migrated.savedAt ?? null;
        this.productCategory = migrated.productCategory ?? null;
        // Note: deviceID stores the ProductSKU ID (TLA SKU number). Legacy designs may have
        // old Device IDs which won't match current ProductSKUs - color restore is skipped in that case.
        this.deviceID = migrated.deviceID ?? null;
        this.stickerSetID = migrated.stickerSetID ?? null;
        this.colorID = migrated.colorID ?? null;
        this.colorAltID = migrated.colorAltID ?? null;
        this.userImageTransform = migrated.userImageTransform ?? null;
        this.uploadedImageURL = migrated.uploadedImageURL ?? null;
        this.transparentImageURL = migrated.transparentImageURL ?? null;
        this.thumbnailImageURL = migrated.thumbnailImageURL ?? null;
        this.__version = migrated.__version ?? CYODesign.CURRENT_VERSION;
    }

    /**
     * Creates a shallow clone of design and debug properties only.
     * Persistence properties (timestamp, URLs, etc.) are not cloned.
     */
    clone() {
        const cloned = new CYODesign();
        cloned.subjectStyle = this.subjectStyle;
        cloned.subjectPlacement = this.subjectPlacement;
        cloned.subjectOutlines = this.subjectOutlines;
        cloned.stickerSet = this.stickerSet;
        cloned.stickerPlacement = this.stickerPlacement;
        cloned.stickerOutlines = this.stickerOutlines;
        cloned.galleryImageID = this.galleryImageID;
        cloned.remixSeed = this.remixSeed;
        cloned.stickerSeed = this.stickerSeed;
        cloned.selectedColor = this.selectedColor;
        cloned.selectedColorAlt = this.selectedColorAlt;
        cloned.walletPrintSurface = this.walletPrintSurface;
        cloned.debugOptions = { ...this.debugOptions };
        return cloned;
    }

    requiresFullRenderComparedTo(other) {
        const { Style, Placement } = CYODesign;

        // if only the style changed, a full render isn't required
        const { subjectStyle: style1, ...obj1WithoutStyle } = this;
        const { subjectStyle: style2, ...obj2WithoutStyle } = other;
        const areEqualWithoutStyle = JSON.stringify(obj1WithoutStyle) === JSON.stringify(obj2WithoutStyle);
        const styleIsDifferent = other.subjectStyle != this.subjectStyle;
        if (styleIsDifferent && areEqualWithoutStyle) {
            return false;
        }

        // Handle changing placement within removeBackground style
        if (other.subjectStyle == Style.removeBackground && this.subjectStyle == Style.removeBackground) {
            const op = other.subjectPlacement;
            const np = this.subjectPlacement;

            // If subject placement changes from center to bottom or vice versa, full render not required
            if ((op == Placement.center && np == Placement.bottom)
                || (op == Placement.bottom && np == Placement.center)) {
                return false;
            }
        }

        return true;
    }

    hasStickerPlacementForPhoto() {
        const { Placement } = CYODesign;
        return this.subjectPlacement == Placement.corner
            || this.subjectPlacement == Placement.border
            || this.subjectPlacement == Placement.pattern;
    }

    /**
     * Returns true if the wallet print surface requires grip-style rendering.
     * Used to determine when to use an alternate compositor with a grip device.
     */
    shouldUseGripRendering(device) {
        if (!device || device.productCategory !== "wallet") return false;
        return this.walletPrintSurface === CYODesign.WalletPrintSurface.split ||
               this.walletPrintSurface === CYODesign.WalletPrintSurface.grip;
    }

    /**
     * Returns the surface mode for a wallet device.
     * Returns null for non-wallet devices.
     */
    getWalletSurfaceMode(device) {
        if (!device || device.productCategory !== "wallet") return null;
        return this.walletPrintSurface || CYODesign.WalletPrintSurface.split;
    }

    /**
     * Returns true if "both" mode requires independent surface rendering.
     * This happens when user image OR stickers are set to border/pattern/corner placements,
     * meaning each surface (wallet and grip) should render its own treatment.
     */
    requiresIndependentSurfaceRendering(device) {
        if (!device || device.productCategory !== "wallet") return false;
        if (this.walletPrintSurface !== CYODesign.WalletPrintSurface.both) return false;

        const { Placement } = CYODesign;
        const placementTypes = [Placement.border, Placement.pattern, Placement.corner];

        const userImageHasPlacement = placementTypes.includes(this.subjectPlacement);
        const stickersHavePlacement = this.stickerSet && placementTypes.includes(this.stickerPlacement);

        return userImageHasPlacement || stickersHavePlacement;
    }

    whatToDrawOnTop(imgUploadVariant, stickerImages) {
        const { Style, Placement, StickerOrPhoto } = CYODesign;

        if (imgUploadVariant == null) {
            return StickerOrPhoto.sticker;
        } else if (stickerImages.length == 0 || this.stickerSet == null) {
            return StickerOrPhoto.photo;
        } else if (this.subjectStyle == Style.full) {
            return StickerOrPhoto.sticker;
        } else if (this.subjectStyle == Style.snapshot) {
            return StickerOrPhoto.sticker;
        } else if (this.subjectStyle == Style.removeBackground) {
            if (this.subjectPlacement == Placement.corner) {
                return StickerOrPhoto.photo;
            }
            if (this.stickerPlacement == Placement.pattern || this.stickerPlacement == Placement.border) {
                return StickerOrPhoto.photo;
            }
            return StickerOrPhoto.sticker;
        }
    }

    placementAndStyleRequiresTransparent() {
        const { Style } = CYODesign;
        return this.subjectStyle == Style.removeBackground;
    }

    placementAndStyleRequiresCircleCrop() {
        return this.subjectPlacement != CYODesign.Placement.bottom &&
            this.subjectPlacement != CYODesign.Placement.corner &&
            this.subjectStyle != CYODesign.Style.snapshot;
    }

    clear() {
        const { Style, Placement } = CYODesign;

        this.subjectStyle = Style.removeBackground;
        this.subjectPlacement = Placement.center;
        this.subjectOutlines = false;
        this.stickerSet = null;
        this.stickerPlacement = Placement.border;
        this.stickerOutlines = false;
        this.galleryImageID = null;
        this.galleryImageDocID = null; // only used for createDesignAsset
        this.remixSeed = null;
        this.selectedColor = null;
        this.selectedColorAlt = null;
        this.walletPrintSurface = CYODesign.WalletPrintSurface.split;
        this.debugOptions = {
            disableRotations: false,
            drawBgs: false,
            showSafeArea: false,
            showBleedInsets: false,
            hideProduct: false,
            hideMask: false,
            hideOverlay: false,
            hideColoredOverlay: false,
            isolateMask: false
        };
    }

    // ==================== Persistence Methods ====================

    /**
     * Factory method to create a CYODesign from the current application state
     * @param {Object} options
     * @param {string} options.productCategory - Product category: "case", "wallet", or "grip"
     * @param {Object|null} options.userImageTransform - Transform data: { scale, rotate, translateX, translateY }
     * Note: Saves Product.device.id as deviceID (ProductSKU ID / TLA SKU number)
     */
    static fromCurrentState({ productCategory, userImageTransform = null }) {
        let randomSeed = null;
        if (window.currentContentRenderer?.randomState) {
            randomSeed = window.currentContentRenderer.randomState.getOriginalSeed();
        }

        const data = {
            __version: CYODesign.CURRENT_VERSION,
            savedAt: Date.now(),
            productCategory: productCategory,
            deviceID: Product.device?.id || null,
            subjectStyle: CYODesign.current.subjectStyle,
            subjectPlacement: CYODesign.current.subjectPlacement,
            stickerSetID: CYODesign.current.stickerSet?.id || null,
            stickerPlacement: CYODesign.current.stickerPlacement,
            galleryImageID: CYODesign.current.galleryImageID,
            remixSeed: randomSeed,
            colorID: CYODesign.current.selectedColor?.id || null,
            colorAltID: CYODesign.current.selectedColorAlt?.id || null,
            walletPrintSurface: CYODesign.current.walletPrintSurface
        };

        // Only save userImageTransform if it's not identity
        if (!isIdentityTransform(userImageTransform)) {
            data.userImageTransform = userImageTransform;
        }

        return new CYODesign(data);
    }

    /**
     * Get all saved designs from localStorage
     * Returns array of CYODesign instances sorted by timestamp (newest first)
     */
    static getSavedDesignsFromLocalStorage() {
        const existing = localStorage.getItem(STORAGE_KEY);
        if (!existing) return [];

        try {
            const savedDesigns = JSON.parse(existing);
            return Object.entries(savedDesigns)
                .map(([timestamp, data]) => new CYODesign({ timestamp, ...data }))
                .sort((a, b) => parseInt(b.timestamp) - parseInt(a.timestamp));
        } catch (e) {
            console.warn("Failed to parse saved designs");
            return [];
        }
    }

    /**
     * Save this design to localStorage
     * Uploads user images to Cloudflare first, then saves design data
     * @param {Object} options
     * @param {HTMLCanvasElement|null} options.designThumbnail - Optional thumbnail canvas (resized to max 600px)
     * Returns true on success, throws error on failure
     */
    async saveToLocalStorage({ designThumbnail = null } = {}) {
        const uploadedImage = imageStorage.getUploadedImage ? imageStorage.getUploadedImage() : imageStorage.uploadedImage;
        const transparentImage = imageStorage.getTransparentImage ? imageStorage.getTransparentImage() : imageStorage.transparentImage;

        if (uploadedImage) {
            const { baseURL } = await uploadImageToCloudflare(uploadedImage);
            this.uploadedImageURL = `${baseURL}/width=2100`;
        }

        if (transparentImage) {
            const { baseURL } = await uploadImageToCloudflare(transparentImage);
            this.transparentImageURL = `${baseURL}/width=2100`;
        }

        if (designThumbnail) {
            const resizedThumbnail = resizeCanvas(designThumbnail, 600);
            const { baseURL } = await uploadImageToCloudflare(resizedThumbnail);
            this.thumbnailImageURL = `${baseURL}/width=600`;
        }

        let savedDesigns = {};
        const existing = localStorage.getItem(STORAGE_KEY);
        if (existing) {
            try {
                savedDesigns = JSON.parse(existing);
            } catch (e) {
                console.warn("Failed to parse existing saved designs, creating new dictionary");
                savedDesigns = {};
            }
        }

        this.timestamp = generateTimestampKey();
        savedDesigns[this.timestamp] = this.toJSONForLocalStorage();

        localStorage.setItem(STORAGE_KEY, JSON.stringify(savedDesigns));

        return true;
    }

    /**
     * Delete this design from localStorage
     * Returns true if deleted, false if not found
     */
    deleteFromLocalStorage() {
        if (!this.timestamp) return false;

        const existing = localStorage.getItem(STORAGE_KEY);
        if (!existing) return false;

        try {
            const savedDesigns = JSON.parse(existing);
            if (savedDesigns[this.timestamp]) {
                delete savedDesigns[this.timestamp];
                localStorage.setItem(STORAGE_KEY, JSON.stringify(savedDesigns));
                return true;
            }
            return false;
        } catch (e) {
            console.warn("Failed to delete design");
            return false;
        }
    }

    /**
     * Restore this design to the application state
     * Loads images from Cloudflare URLs and restores design options to CYODesign.current
     * @param {Object} callbacks - Callbacks for sticker set lookup
     * @returns {Object} - Contains userImageTransform for compositor
     */
    async restore(callbacks = {}) {
        const { onStickerSetNeeded } = callbacks;

        // Load images from Cloudflare URLs
        if (this.uploadedImageURL) {
            const uploadedImage = await loadImageFromURL(this.uploadedImageURL);
            imageStorage.setUploadedImage(uploadedImage);
        } else {
            imageStorage.setUploadedImage(null);
        }

        if (this.transparentImageURL) {
            const transparentImage = await loadImageFromURL(this.transparentImageURL);
            imageStorage.setTransparentImage(transparentImage);
        } else {
            imageStorage.setTransparentImage(null);
        }

        // Restore design options to CYODesign.current
        CYODesign.current.subjectStyle = this.subjectStyle;
        CYODesign.current.subjectPlacement = this.subjectPlacement;
        CYODesign.current.stickerPlacement = this.stickerPlacement;
        CYODesign.current.galleryImageID = this.galleryImageID;
        CYODesign.current.walletPrintSurface = this.walletPrintSurface;

        // Restore random seed for deterministic sticker placement, etc.
        if (this.remixSeed !== null && this.remixSeed !== undefined) {
            CYODesign.current.remixSeed = this.remixSeed;
        }

        // Load gallery background image if present
        let hasGalleryImage = false;
        if (this.galleryImageID) {
            const dpr = Math.round(window.devicePixelRatio);
            const galleryImage = await fetchCFImage(this.galleryImageID, 400, "webp", 85, dpr);
            imageStorage.setBackgroundImage(galleryImage);
            hasGalleryImage = true;
        } else {
            imageStorage.setBackgroundImage(null);
        }

        // Restore sticker set (requires feed lookup)
        let stickerSet = null;
        if (this.stickerSetID && onStickerSetNeeded) {
            stickerSet = await onStickerSetNeeded(this.stickerSetID);
            CYODesign.current.stickerSet = stickerSet;
        } else {
            CYODesign.current.stickerSet = null;
        }

        return {
            userImageTransform: this.userImageTransform,
            hasGalleryImage: hasGalleryImage,
            hasUserImage: !!this.uploadedImageURL,
            stickerSet: stickerSet,
            colorID: this.colorID,
            colorAltID: this.colorAltID
        };
    }

    /**
     * Convert to plain object for JSON serialization (excludes timestamp which is used as key)
     * Note: deviceID is the ProductSKU ID used when design was created. Used to determine
     * if color selection can be restored (only restored if current ProductSKU matches).
     */
    toJSONForLocalStorage() {
        const obj = {
            __version: this.__version,
            savedAt: this.savedAt,
            productCategory: this.productCategory,
            deviceID: this.deviceID,
            subjectStyle: this.subjectStyle,
            subjectPlacement: this.subjectPlacement,
            stickerSetID: this.stickerSetID,
            stickerPlacement: this.stickerPlacement,
            galleryImageID: this.galleryImageID,
            remixSeed: this.remixSeed
        };

        if (this.userImageTransform) {
            obj.userImageTransform = this.userImageTransform;
        }
        if (this.uploadedImageURL) {
            obj.uploadedImageURL = this.uploadedImageURL;
        }
        if (this.transparentImageURL) {
            obj.transparentImageURL = this.transparentImageURL;
        }
        if (this.thumbnailImageURL) {
            obj.thumbnailImageURL = this.thumbnailImageURL;
        }
        if (this.colorID) {
            obj.colorID = this.colorID;
        }
        if (this.colorAltID) {
            obj.colorAltID = this.colorAltID;
        }
        if (this.walletPrintSurface) {
            obj.walletPrintSurface = this.walletPrintSurface;
        }

        return obj;
    }
}

// Initialize the singleton after class definition
CYODesign.current = new CYODesign();

function generateTimestampKey() {
    return Math.floor(Date.now() / 1000).toString();
}

function resizeCanvas(sourceCanvas, maxSize) {
    const { width, height } = sourceCanvas;

    if (width <= maxSize && height <= maxSize) {
        const copy = document.createElement("canvas");
        copy.width = width;
        copy.height = height;
        const ctx = copy.getContext("2d");
        ctx.drawImage(sourceCanvas, 0, 0);
        return copy;
    }

    const scale = Math.min(maxSize / width, maxSize / height);
    const newWidth = Math.round(width * scale);
    const newHeight = Math.round(height * scale);

    const resized = document.createElement("canvas");
    resized.width = newWidth;
    resized.height = newHeight;
    const ctx = resized.getContext("2d");
    ctx.drawImage(sourceCanvas, 0, 0, newWidth, newHeight);
    return resized;
}

async function loadImageFromURL(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = url;
    });
}

export { CYODesign };
